import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();
  const { checkAuthStatus, csrfToken, fetchCsrfToken, supabase } = useAuth();
console.log('Signin  rendering');
// In SignIn.jsx
 const handleSignIn = async (e) => {
  console.log('Signin with password started');
  e.preventDefault();
  setLoading(true);
  setError(null);
  try {
    // 1. Authenticate via your custom endpoint (sets cookies)
    const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-csrf-token': csrfToken
      },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });
    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Sign-in failed');
    }
    const data = await response.json(); // Contains user and session

    // 2. *** AWAIT Supabase client sync - CRITICAL for RPC readiness ***
    console.log('About to await setSession for client sync.');
    const { error: sessionError } = await supabase.auth.setSession({
      access_token: data.session.access_token,
      refresh_token: data.session.refresh_token,
    });
    if (sessionError) {
      console.warn("Error during supabase.auth.setSession:", sessionError.message);
      throw new Error('Session sync failed: ' + sessionError.message);
    }
    console.log('setSession resolved successfully - Auth state synced.');

    // 3. NOW navigate - Session fully ready
    console.log('Signin fetch complete. Navigating to signin-check...');
    navigate('/signin-check'); // Use the checking route
  } catch (err) {
    console.error('SignIn error:', err.message);
    setError(err.message);
  } finally {
    setLoading(false); // Always stop loading
  }
};
 const handleGoogleSignIn = async () => {
  console.log('Signin with Google started');
  setError(null);
  setLoading(true);
  try {
    const { data, error: signInError } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      scopes: 'openid email profile https://www.googleapis.com/auth/userinfo.email',
      redirectTo: `${window.location.origin}/signin-check`,
      queryParams: { prompt: 'consent' }
    },
    });

    if (signInError) {
      throw new Error(signInError.message);
    }
  } catch (err) {
    setError(err.message || 'Google Sign-In failed to initiate.');
    setLoading(false);
  }
};
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (resetError) {
        throw new Error(`Password reset failed: ${resetError.message}`);
      }
      setSuccess('Password reset email sent! (if it exists) Check your inbox.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-grow p-4">
        <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-center">Sign In</h2>
          {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
          {success && <p className="text-green-500 mb-4 text-center">{success}</p>}
          {!showForgotPassword ? (
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <label className="block mb-1 text-gray-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-gray-700">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">
                    OR
                  </span>
                </div>
              </div>
              
              <button
                type="button" // Important: keep this button type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition duration-200 disabled:bg-gray-200"
                disabled={loading}
              >
                {/* FIXED: Add Google logo SVG (standard "G" icon, white on blue for branding) */}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="mr-2 fill-current text-red-500">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="ml-2 text-gray-700 font-medium">Sign in with Google</span>
              </button>
              
              <p className="text-center mt-4">
                <button type="button" onClick={() => setShowForgotPassword(true)} className="text-blue-500 hover:underline">
                  Forgot Password?
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block mb-1 text-gray-700">Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Reset Password'}
              </button>
              <p className="text-center mt-4">
                <button type="button" onClick={() => setShowForgotPassword(false)} className="text-blue-500 hover:underline">
                  Back to Sign In
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default SignIn;